import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpClient, APIResponseType } from './types';
import { handleLogout, header } from 'utils/auth';
import { getAgentType, shouldIncludeAgentTypeInUrl } from 'app/utils/agentType';

import { ensureCsrfToken } from 'utils/csrf';

export class AxiosAdapter implements HttpClient {
  private axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  constructor() {
    this.axiosInstance.interceptors.request.use(
      async config => {
        const method = (config.method || 'get').toUpperCase();
        if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
          try {
            const csrf = await ensureCsrfToken();
            if (csrf) {
              config.headers['X-CSRF-Token'] = csrf;
            }
          } catch (e) {
            console.warn('CSRF for request failed', e);
          }
        }
        const auth = header();
        if (auth) {
          config.headers['Authorization'] = auth;
        }

        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        // Inject the globally selected agent_type as a query param only for
        // URL paths in the allowlist. Caller-supplied values always win.
        const agentType = getAgentType();
        if (agentType && shouldIncludeAgentTypeInUrl(config.url || '')) {
          const existingParams = (config.params || {}) as Record<string, any>;
          const hasInParams =
            existingParams && Object.prototype.hasOwnProperty.call(existingParams, 'agent_type');
          const hasInUrl = !!(config.url && /[?&]agent_type=/.test(config.url));

          if (!hasInParams && !hasInUrl) {
            config.params = { ...existingParams, agent_type: agentType };
          }
        }

        return config;
      },
      error => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          handleLogout();
        }
        return this.handleError(error);
      },
    );
  }

  private extractServerErrorMessage(data: unknown): string | undefined {
    if (data == null || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    const asNonEmptyString = (v: unknown): string | undefined =>
      typeof v === 'string' && v.trim() ? v.trim() : undefined;

    const fromMessage = asNonEmptyString(d.message);
    if (fromMessage) return fromMessage;

    const fromErrorField = asNonEmptyString(d.error);
    if (fromErrorField) return fromErrorField;

    if (d.error !== null && typeof d.error === 'object') {
      const nested = asNonEmptyString(
        (d.error as Record<string, unknown>).message,
      );
      if (nested) return nested;
    }

    return asNonEmptyString(d.detail);
  }

  /**
   * Normalizes axios failures into a single rejection shape across the app.
   *
   * Cancelled requests (axios cancel / AbortController — frequently triggered
   * by React Query teardown during navigation or `removeQueries`) are
   * re-rejected as-is; surfacing them as runtime errors turns into
   * `[object Object]` in the CRA dev overlay. All other failures are wrapped
   * in real `Error` instances so `err.message` is readable, while the legacy
   * extra fields (`statusCode`, `code`, `rawError`, `response`, `responseData`,
   * `originalError`, etc.) are still attached for callers that depend on them.
   */
  private handleError(error: any): Promise<never> {
    if (
      axios.isCancel(error) ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError' ||
      error?.code === 'ERR_CANCELED'
    ) {
      return Promise.reject(error);
    }

    if (error.response) {
      console.error('Response Error:', error.response.data);
      const responseData = error.response.data || {};
      const rd = responseData as Record<string, unknown>;
      const message =
        this.extractServerErrorMessage(error.response.data) || 'An error occurred';
      const rejected = new Error(message) as Error & {
        code?: unknown;
        errorCode?: unknown;
        rawError?: unknown;
        statusCode?: number;
        statusText?: string;
        response?: unknown;
        responseData?: unknown;
        originalError?: unknown;
      };
      rejected.code = rd.code ?? rd.errorCode;
      rejected.errorCode = rd.errorCode ?? rd.code;
      rejected.rawError = responseData;
      rejected.statusCode = error.response.status;
      rejected.statusText = error.response.statusText;
      rejected.response = error.response;
      rejected.responseData = error.response.data;
      rejected.originalError = error;
      return Promise.reject(rejected);
    } else if (error.request) {
      console.error('Request Error:', error.request);
      const rejected = new Error('No response received from the server') as Error & {
        originalError?: unknown;
      };
      rejected.originalError = error;
      return Promise.reject(rejected);
    } else {
      console.error('Error:', error?.message);
      const rejected = new Error(error?.message || 'An error occurred') as Error & {
        originalError?: unknown;
      };
      rejected.originalError = error;
      return Promise.reject(rejected);
    }
  }

  async get<T>(url: string, config?: Record<string, any>): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.get(url, config);

      if (config?.responseType === 'blob' || response.data instanceof Blob) {
        let filename: string | undefined;

        if (response.headers && response.headers['content-disposition']) {
          const contentDisposition = response.headers['content-disposition'];
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        return {
          body: response.data as unknown as T,
          statusCode: response.status,
          statusText: response.statusText,
          message: response.statusText,
          filename: filename,
        };
      }

      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.post(url, data, config);

      if (config?.responseType === 'blob' || response.data instanceof Blob) {
        let filename: string | undefined;

        if (response.headers && response.headers['content-disposition']) {
          const contentDisposition = response.headers['content-disposition'];
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        return {
          body: response.data as unknown as T,
          statusCode: response.status,
          statusText: response.statusText,
          message: response.statusText,
          filename: filename,
        };
      }

      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }
}
